import { useEffect, useRef, useState, type ChangeEvent } from "react";

import type { ProjectSoftwareRule } from "@zentrack/domain";
import type { TrackingCapability } from "@zentrack/desktop-api";
import {
  Badge,
  Banner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
} from "@zentrack/ui";

export interface ProjectEditorDialogProps {
  clientName: string;
  initialName?: string;
  initialSoftwareRules?: ProjectSoftwareRule[];
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, softwareRules: ProjectSoftwareRule[]) => Promise<void>;
  open: boolean;
  title: string;
  trackingCapability: TrackingCapability;
}

const MIN_PROJECT_NAME_LENGTH = 4;

/**
 * The browser cannot discover installed apps, so the project editor here only
 * shows the project's configured software rules as context (with a clear
 * capability alert) instead of a searchable app catalog. Rules can still be
 * edited from the desktop app.
 */
export function ProjectEditorDialog({
  clientName,
  initialName = "",
  initialSoftwareRules = [],
  onOpenChange,
  onSave,
  open,
  title,
  trackingCapability,
}: ProjectEditorDialogProps) {
  const [name, setName] = useState(initialName);
  const [hasEditedName, setHasEditedName] = useState(false);
  const initializedRef = useRef(false);

  const trimmedName = name.trim();
  const nameValidationMessage =
    trimmedName.length > 0 && trimmedName.length < MIN_PROJECT_NAME_LENGTH
      ? `Project name must be at least ${MIN_PROJECT_NAME_LENGTH} characters.`
      : null;
  const canSave = trimmedName.length >= MIN_PROJECT_NAME_LENGTH;

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;
    setName(initialName);
    setHasEditedName(false);
  }, [initialName, open]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(92vw,640px)]">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSave) {
              return;
            }

            void onSave(trimmedName, initialSoftwareRules);
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>Projects stay under the current client.</DialogDescription>
          </DialogHeader>

          <FieldGroup className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Client</FieldLabel>
              <div className="rounded-lg border border-border/80 bg-secondary px-4 py-3 text-sm text-foreground">
                {clientName}
              </div>
            </Field>
            <Field>
              <FieldLabel htmlFor="project-name">Project name</FieldLabel>
              <Input
                autoFocus
                id="project-name"
                onBlur={() => setHasEditedName(true)}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  if (!hasEditedName) {
                    setHasEditedName(true);
                  }
                  setName(event.target.value);
                }}
                value={name}
              />
              {hasEditedName && nameValidationMessage ? (
                <p className="text-sm text-destructive">{nameValidationMessage}</p>
              ) : null}
            </Field>
          </FieldGroup>

          <Field>
            <FieldLabel>Tracked software</FieldLabel>
            <FieldDescription>
              {initialSoftwareRules.length > 0
                ? `${initialSoftwareRules.length} ${
                    initialSoftwareRules.length === 1 ? "app" : "apps"
                  } configured. Foreground-app filtering only runs in the desktop app.`
                : "All apps count. Foreground-app filtering only runs in the desktop app."}
            </FieldDescription>

            {initialSoftwareRules.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {initialSoftwareRules.map((rule) => (
                  <Badge key={`${rule.matcherType}:${rule.matchValue}`} tone="warning">
                    {rule.displayName}
                  </Badge>
                ))}
              </div>
            ) : null}

            {trackingCapability.status !== "supported" ? (
              <Banner
                message="Software rules can't be added or edited in the browser because installed-app discovery requires the ZenTrack desktop app. Existing rules are preserved and shown above; open the project in the desktop app to change them."
                tone="warning"
              />
            ) : null}
          </Field>

          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={!canSave} type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
